import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  TextField,
  OutlinedInput,
  styled,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { ReactComponent as PaginationFirst } from "assets/images/icons/icn_pagination_first.svg";
import { ReactComponent as PaginationLast } from "assets/images/icons/icn_pagination_last.svg";
import { ReactComponent as PaginationNext } from "assets/images/icons/icn_pagination_next.svg";
import { ReactComponent as PaginationPrev } from "assets/images/icons/icn_pagination_prev.svg";
import { ReactComponent as PaginationDown } from "assets/images/icons/icn_pagination_chevron_down.svg";

import localStorageUtil from "utils/localStorageUtil";


const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  flexWrap: "wrap",
}));

const IconBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.primary.whiteSecound}`,
  borderRadius: theme.shape.customBorderRadius[1],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  padding: "10px",
  backgroundColor: theme.palette.primary.transparent,
}));

const TransparentIconButton = styled(IconButton)(({ theme }) => ({
  padding: 0,
  backgroundColor: theme.palette.primary.transparent,
  "&:hover": {
    backgroundColor: theme.palette.primary.transparent,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "47px",
  "& .MuiOutlinedInput-root": {
    height: "42px",
    borderRadius: theme.shape.customBorderRadius[1],
    "& fieldset": {
      borderColor: theme.palette.primary.borderGreyPrimary,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.mainHover,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
  "& input": {
    textAlign: "center",
    fontSize: theme.typography.customFontSize[3],
    color: theme.palette.primary.greyPrimary,
  },
}));

const StyledOutlinedInput = styled(OutlinedInput)(({ theme }) => ({
  height: "42px",
  borderRadius: theme.shape.customBorderRadius[1],
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.borderGreyPrimary,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.mainHover,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },
}));


const PaginationField = ({
  name,
  pageCount,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const theme = useTheme();
  const [inputPage, setInputPage] = useState(pageIndex + 1);
  const [localPageSize, setLocalPageSize] = useState(() => {
    return localStorageUtil.getItemField('resultsPerPageObject', name) || pageSize;
  });


  useEffect(() => {
    if (!localStorageUtil.getItemField('resultsPerPageObject', name) && pageSize) {
      localStorageUtil.updateItemField('resultsPerPageObject', name, pageSize);
    }
  }, [])

  useEffect(() => {
    setInputPage(pageIndex + 1);
  }, [pageIndex]);

  function handlePerPage(event) {
    const newSize = parseInt(event.target.value, 10);
    setLocalPageSize(newSize);
    onPageSizeChange(newSize);
    localStorageUtil.updateItemField('resultsPerPageObject', name, newSize);
  }

  const handleInputChange = (e) => {
    setInputPage(e.target.value);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      let newPage = parseInt(inputPage, 10);
      if (!isNaN(newPage)) {
        newPage = Math.min(Math.max(newPage, 1), pageCount);
        onPageChange(newPage - 1);
      }
    }
  };

  return (
    <Container>
      <Box display="flex" alignItems="center">
        <IconBox>
          <TransparentIconButton
            onClick={() => onPageChange(0)}
            disabled={pageIndex === 0}
          >
            <PaginationFirst />
          </TransparentIconButton>
        </IconBox>
        <IconBox sx={{ margin: "0 10px" }}>
          <TransparentIconButton
            onClick={() => onPageChange(pageIndex - 1)}
            disabled={pageIndex === 0}
          >
            <PaginationPrev />
          </TransparentIconButton>
        </IconBox>

        <Box display="flex" alignItems="center" gap={1}>
          <Typography fontSize={theme.typography.customFontSize[3]}>
            Page
          </Typography>
          <StyledTextField
            value={inputPage}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            inputProps={{
              min: 1,
              max: isNaN(pageCount) ? 1 : pageCount,
            }}
          />
          <Typography fontSize={theme.typography.customFontSize[3]}>
            of {pageCount}
          </Typography>
        </Box>

        <IconBox sx={{ marginLeft: "10px" }}>
          <TransparentIconButton
            onClick={() => onPageChange(pageIndex + 1)}
            disabled={pageIndex >= pageCount - 1}
          >
            <PaginationNext />
          </TransparentIconButton>
        </IconBox>
        <IconBox sx={{ margin: "0 10px" }}>
          <TransparentIconButton
            onClick={() => onPageChange(pageCount - 1)}
            disabled={pageIndex >= pageCount - 1}
          >
            <PaginationLast />
          </TransparentIconButton>
        </IconBox>
      </Box>

      <Typography fontSize={theme.typography.customFontSize[3]}>
        Results per page:
      </Typography>

      <Select
        value={localPageSize}
        onChange={handlePerPage}
        input={<StyledOutlinedInput />}
        IconComponent={PaginationDown}
      >
        {[10, 20, 50, 100].map((size) => (
          <MenuItem key={size} value={size}>
            {size}
          </MenuItem>
        ))}
      </Select>
    </Container>
  );
};

export default PaginationField;